import GroupsIcon from "@mui/icons-material/Groups";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  ListItem,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import BasicDialog from "../../common/components/functional/dialogs/BasicDialog";
import { AutocompleteBaseSingle } from "../../common/components/functional/fields/autocompleteBase/AutocompleteBaseSingle";
import ExpertsAutocomplete from "../../common/components/functional/fields/expertsAutocomplete/ExpertsAutocomplete";
import { NameIdOption } from "../../common/types/commonTypes";
import {
  ClosingMessageLanguage,
  DEFAULT_CLOSE_CONVERSATION_OPTIONS,
} from "./conversationClosingMessage";
import { fetchTeams } from "./messagingActions";
import {
  selectMyTeamIds,
  selectSelectedConversation,
  selectTeams,
  selectTeamsStatus,
} from "./messagingSlice";
import {
  buildTransferTeamChoices,
  canReceiveTransferredConversation,
  getTransferMessage,
  OWN_TEAM_CHOICE_KEY,
  TransferConversationOptions,
  transferExcludedTeamId,
  TransferMessageKind,
  TransferTeamChoice,
} from "./transferConversation";

type Props = {
  open: boolean;
  teamTransferDisabled: boolean;
  onClose: () => void;
  onConfirm: (options: TransferConversationOptions) => void;
};

const ForwardConversationDialog = ({
  open,
  teamTransferDisabled,
  onClose,
  onConfirm,
}: Props) => {
  const { t } = useTranslation(["messaging", "common"]);
  const dispatch = useAppDispatch();
  const myTeamIds = useAppSelector(selectMyTeamIds);
  const teams = useAppSelector(selectTeams);
  const teamsStatus = useAppSelector(selectTeamsStatus);
  const selectedConversation = useAppSelector(selectSelectedConversation);

  // current user's own team is excluded
  const excludedTeamId = transferExcludedTeamId(selectedConversation);
  const userId = useAppSelector((state) => state.user.user?.id);
  const [teamChoice, setTeamChoice] = useState<TransferTeamChoice | null>(null);
  const [expert, setExpert] = useState<NameIdOption[]>([]);
  const [sendTransferMessage, setSendTransferMessage] = useState(
    DEFAULT_CLOSE_CONVERSATION_OPTIONS.sendClosingMessage,
  );
  const [language, setLanguage] = useState<ClosingMessageLanguage>(
    DEFAULT_CLOSE_CONVERSATION_OPTIONS.language,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedExpert = expert[0];
  const expertSelected = !!selectedExpert && selectedExpert.id !== userId;
  const hasDestination =
    (!!teamChoice && !teamChoice.disabled) || expertSelected;
  const messageKind: TransferMessageKind = teamChoice ? "team" : "expert";
  const teamChoices = useMemo(
    () =>
      buildTransferTeamChoices({
        teams,
        myTeamIds,
        ownTeamEnabled: !teamTransferDisabled,
        ownTeamLabel: t("messaging:ownTeam"),
        excludedTeamId,
      }),
    [teams, myTeamIds, teamTransferDisabled, excludedTeamId, t],
  );

  useEffect(() => {
    if (!open) return;
    dispatch(fetchTeams());
  }, [dispatch, open]);

  useEffect(() => {
    if (open) return;
    setTeamChoice(null);
    setExpert([]);
    setSendTransferMessage(
      DEFAULT_CLOSE_CONVERSATION_OPTIONS.sendClosingMessage,
    );
    setLanguage(DEFAULT_CLOSE_CONVERSATION_OPTIONS.language);
    setIsSubmitting(false);
  }, [open]);

  useEffect(() => {
    if (!teamChoice) return;
    const choice = teamChoices.find((item) => item.key === teamChoice.key);
    if (!choice || choice.disabled) setTeamChoice(null);
  }, [teamChoice, teamChoices]);

  const handleConfirm = () => {
    if (isSubmitting || !hasDestination || (!teamChoice && !selectedExpert)) {
      return;
    }
    setIsSubmitting(true);
    onConfirm({
      destination: teamChoice
        ? { type: "TEAM", ids: teamChoice.teamIds }
        : { type: "EXPERT", ids: [selectedExpert.id] },
      sendTransferMessage,
      language,
    });
  };

  return (
    <BasicDialog
      open={open}
      title={t("messaging:forwardConversation")}
      onCancel={onClose}
      onConfirm={handleConfirm}
      confirmDisabled={!hasDestination || isSubmitting}
      confirmLabel={t("messaging:forwardConversation")}
      variant="white"
      PaperProps={{ sx: { width: "560px" } }}
      content={
        <Stack spacing={2}>
          <ExpertsAutocomplete
            multiple={false}
            value={expert}
            disabled={!!teamChoice}
            onChange={(value) => {
              setExpert(value ?? []);
              if (value?.length) setTeamChoice(null);
            }}
            includeExpert={canReceiveTransferredConversation}
            placeholder={t("messaging:transferToExpert")}
          />
          <AutocompleteBaseSingle<TransferTeamChoice>
            value={teamChoice}
            options={teamChoices}
            onChange={(choice) => {
              setTeamChoice(choice);
              if (choice) setExpert([]);
            }}
            getOptionLabel={(option) => option.label}
            getOptionDisabled={(option) => option.disabled}
            isOptionEqualToValue={(option, value) => option.key === value.key}
            renderOption={(props, option) => (
              <ListItem {...props} key={option.key}>
                {option.key === OWN_TEAM_CHOICE_KEY && (
                  <GroupsIcon
                    fontSize="small"
                    sx={{ mr: 1, color: "primary.main" }}
                  />
                )}
                {option.label}
              </ListItem>
            )}
            disabled={expertSelected}
            loading={teamsStatus === "pending" && teams.length === 0}
            placeholder={t("messaging:transferToTeam")}
          />
          <FormControlLabel
            sx={{ alignSelf: "flex-start" }}
            control={
              <Checkbox
                checked={sendTransferMessage}
                onChange={(_, checked) => setSendTransferMessage(checked)}
                sx={{ ml: -1.5 }}
              />
            }
            label={t("messaging:sendTransferMessage")}
          />
          <FormControl disabled={!sendTransferMessage || !hasDestination}>
            <FormLabel id="transfer-message-language-label">
              {t("messaging:transferMessageLanguage")}
            </FormLabel>
            <RadioGroup
              row
              aria-labelledby="transfer-message-language-label"
              name="transfer-message-language"
              value={language}
              onChange={(event) =>
                setLanguage(event.target.value as ClosingMessageLanguage)
              }
            >
              <FormControlLabel
                value="fi"
                control={<Radio />}
                label={t("common:fi")}
              />
              <FormControlLabel
                value="en"
                control={<Radio />}
                label={t("common:en")}
              />
            </RadioGroup>
          </FormControl>
          {sendTransferMessage && hasDestination && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: "pre-wrap" }}
            >
              {getTransferMessage(messageKind, language)}
            </Typography>
          )}
        </Stack>
      }
    />
  );
};

export default ForwardConversationDialog;
